import React, { lazy, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AASTEPS } from '..';
import { AppContext } from '../../../App';
import { FlowType, isLogEnabled } from '../../../Utils/constants';
import {
  EVENTCODES,
  pushEventToParent,
  pushEventToPosthog,
} from '../../../Utils/eventUtils';
import { ROUTES } from '../../../Utils/routes';
import { getTenant } from '../../../Utils/storageUtils';
import { LOADERTYPE, TenantType } from '../../../Utils/types';
import { VIEWTYPE } from '../../HomePage';
import { captureException } from '../../../Utils/sentryUtils';

export const loadPBAA = () => import('../../PaisaBazaar/AA');
export const loadYBAA = () => import('../../YesBank/AA');
export const loadJFSAA = () => import('../../JFS/AA');
export const loadAUAA = () => import('../../AUBank/AA');

const PBAA = lazy(loadPBAA);
const YesBankAA = lazy(loadYBAA);
const JFSAA = lazy(loadJFSAA);
const AUAA = lazy(loadAUAA);

const MahindraAA = lazy(() => {
  return import('../../Mahindra/AA');
});
export enum AccountDiscoveryStatus {
  Default = 'Default',
  AccountsDiscovered = 'AccountsDiscovered',
  NoAccountsFound = 'NoAccountsFound',
}

interface Props {
  gotoRoot: any;
  goBackOnJourneyFailure: any;
  setAAInternalStep: any;
  isInfoAvailable: any;
  setIsInfoAvailable: any;
  redirectionURL: any;
  setPartialLoading: any;
  isPartial: any;
  partialLoading: any;
}

const AAWrapper: React.FC<Props> = ({
  setIsInfoAvailable,
  gotoRoot,
  goBackOnJourneyFailure,
  setAAInternalStep,
  isInfoAvailable,
  redirectionURL,
  isPartial,
  partialLoading,
  setPartialLoading,
}) => {
  const {
    isAdditionalAccountJourney,
    setIsLoading,
    isLoanJourney,
    setLoaderType,
    setAnalyticsStatus,
    setIsAccountDetailsFetched,
    setCurrentRoute,
    setSelectedFIP,
    statusData,
    setViewType,
    backEventSent,
    currentFlowType,
    setIsHeaderVisible,
    setIsBottomNavbarVisible,
    setBackButtonHandler,
    setShowToastAA,
  }: any = useContext(AppContext);

  let consentApprovalCount = 0;

  const navigate = useNavigate();
  const isJourneyCompletedEventCame = useRef(false);
  const [showHeader, setShowHeader] = useState(false);
  const [accountDiscoveryStatus, setAccountDiscoveryStatus] =
    useState<AccountDiscoveryStatus>(AccountDiscoveryStatus.Default);

  const updatedStatusDefaultConsentCreation = () => {
    setAccountDiscoveryStatus(AccountDiscoveryStatus.Default);
    setIsInfoAvailable(true);
    setPartialLoading(true);
  };

  const enableLoaderAndRouteToDashboard = () => {
    setIsLoading(true);
    setAnalyticsStatus('PENDING');
    setIsAccountDetailsFetched(false);
    setLoaderType(LOADERTYPE.DATALOADER);
    setSelectedFIP([]);
    gotoRoot();

    if (getTenant() === TenantType.MAHINDRA) {
      setViewType(VIEWTYPE.BANKSUMMARYVIEW);
    }
  };

  const handleRoutingOnJourneyCompleted = () => {
    updatedStatusDefaultConsentCreation();

    if (
      !isJourneyCompletedEventCame.current &&
      !statusData.current.approvedConsentsAvailable
    ) {
      if (getTenant() === TenantType.JFS) {
        setCurrentRoute(ROUTES.JFS_WELCOME);
        return;
      }
    }

    if (
      statusData.current &&
      statusData.current.accountDetails &&
      statusData.current.accountDetails.length > 0
    ) {
      gotoRoot();
    } else if (
      !isJourneyCompletedEventCame.current &&
      getTenant() !== TenantType.JFS &&
      getTenant() !== TenantType.MAHINDRA
    ) {
      setAAInternalStep(AASTEPS.FIP_SELECTION);
    } else {
      enableLoaderAndRouteToDashboard();
    }
  };
  useEffect(() => {
    if (getTenant() === TenantType.JFS) {
      setBackButtonHandler(() => {
        return () => {
          let frame: any = document.getElementById('aa-wrapper-iframe');
          if (frame && frame.contentWindow) {
            frame.contentWindow.postMessage(
              { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
              '*'
            );
          } else {
            gotoRoot();
          }
        };
      });
    }
  }, []);

  const handleSDKEvents = (event: any) => {
    const { data } = event;

    if (isLogEnabled) {
      console.log('Inside AA HomePage', event.data ? event.data : '');
    }

    try {
      if (typeof data === 'string') {
        const parsedData = JSON.parse(data);
        if (parsedData.action === 'handleIframeBackAction') {
          let frame: any = document.getElementById('aa-wrapper-iframe');
          if (frame && frame.contentWindow) {
            frame.contentWindow.postMessage(
              { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
              '*'
            );
          }
        }
      }
    } catch (error) {
      captureException(error);
    }
    if (event && event.data && (event.data.otp || event.data.OTP)) {
      const otp = event.data.otp || event.data.OTP;
      let meta = undefined;
      if (getTenant() === TenantType.JFS) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = { fiType: FlowType[currentFlowType] };
        }
      }
      pushEventToPosthog({
        eventCode: EVENTCODES.RECEIVED_AUTO_READ_EVENT,
        meta: meta,
      });
      let frame: any = document.getElementById('aa-wrapper-iframe');
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ OTP: otp }, '*');
        frame.contentWindow.postMessage({ otp: otp }, '*');
      }
    } else if (
      event &&
      event.data &&
      event.data.action &&
      event.data.action === 'handleIframeBackAction'
    ) {
      let frame: any = document.getElementById('aa-wrapper-iframe');
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage(
          { PARENT_COMMAND: 'BACK_BUTTON_CLICKED' },
          '*'
        );
      }
    } else if (
      event.data.eventCode &&
      (event.data.eventCode === 'USER_LOGGEDIN' ||
        event.data.eventCode === 'USER_SIGNEDUP')
    ) {
      setIsInfoAvailable(false);
    }

    if (event.data.eventCode && event.data.eventCode !== 'JOURNEY_COMPLETED') {
      let meta = undefined;
      if (
        event.data.eventCode === 'CONSENT_APPROVED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_APPROVED_FAILED'
      ) {
        meta = {
          consentHandle: event.data.consentHandle[consentApprovalCount],
        };
        consentApprovalCount++;
      }

      if (getTenant() === TenantType.JFS) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = {
            ...meta,
            fiType: FlowType[currentFlowType],
          };
        }
      }
      pushEventToParent({
        eventCode: event.data.eventCode,
        errorMessage: event.data.errorMessage,
        errorStatus: event.data.errorStatus,
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_${event.data.eventCode}`,
            }
          : {}),
      });
    } else if (
      event.data.eventCode &&
      event.data.eventCode === 'JOURNEY_COMPLETED' &&
      (isLoanJourney.current || getTenant() === TenantType.JFS)
    ) {
      let meta = undefined;
      if (getTenant() === TenantType.JFS) {
        if (currentFlowType) {
          meta = {
            fiType: FlowType[currentFlowType],
          };
        }
      }
      pushEventToParent({
        eventCode: event.data.eventCode,
        errorMessage: event.data.errorMessage,
        errorStatus: event.data.errorStatus,
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_${event.data.eventCode}`,
            }
          : {}),
      });
    }
    if (event.data.eventCode === 'IFRAME_SUCCESS') {
      if (isPartial.current) {
        setPartialLoading(false);
      }
      setIsLoading(false);
    } else if (
      event.data.eventCode === 'CONSENT_APPROVAL_INITIATED' &&
      isLoanJourney.current
    ) {
      setIsLoading(true);
      setAnalyticsStatus('PENDING');
      setIsAccountDetailsFetched(false);
      setLoaderType(LOADERTYPE.DATALOADER);
    } else if (event.data.eventCode === 'JOURNEY_COMPLETED') {
      if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA
      ) {
        handleRoutingOnJourneyCompleted();
      } else {
        if (!isLoanJourney.current) {
          setAnalyticsStatus('PENDING');
          setIsAccountDetailsFetched(false);
          setTimeout(() => {
            navigate('/home', { replace: true });
            gotoRoot();
          }, 500);
        }
      }
    } else if (event.data.eventCode === 'CONSENT_APPROVED_SUCCESS') {
      if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.JFS
      ) {
        isJourneyCompletedEventCame.current = true;
      }
    } else if (event.data.eventCode === 'JOURNEY_SKIPPED') {
      if (
        getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA
      ) {
        handleRoutingOnJourneyCompleted();
      } else if (getTenant() === TenantType.JFS) {
        handleRoutingOnJourneyCompleted();
      } else if (getTenant() === TenantType.AU) {
        gotoRoot();
      } else {
        if (!statusData.current.approvedConsentsAvailable) {
          pushEventToParent({
            eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
          });
          backEventSent.current = true;
        }
        setIsLoading(false);
        goBackOnJourneyFailure();
      }
    } else if (event.data.eventCode === 'ADD_ANOTHER_BANK') {
      setIsLoading(false);
      setAAInternalStep(AASTEPS.FIP_SELECTION);
    } else if (event.data.eventCode === 'CONSENT_APPROVAL_INITIATION_FAILED') {
      setIsLoading(false);
      if (getTenant() === TenantType.JFS) {
        setShowToastAA(true);
        if (statusData.current.approvedConsentsAvailable) {
          setCurrentRoute(ROUTES.DASHBOARD);
          gotoRoot();
        } else {
          setCurrentRoute(ROUTES.JFS_WELCOME);
          navigate('/welcome');
        }
      } else {
        setAAInternalStep(AASTEPS.CONSENT_FAILURE);
      }
    } else if (event.data.eventCode === 'NO_ACCOUNTS_FOUND') {
      setAccountDiscoveryStatus(AccountDiscoveryStatus.NoAccountsFound);
    } else if (
      event.data.eventCode === 'ACCOUNTS_DISCOVERED' ||
      event.data.eventCode === 'LINKED_ACCOUNT_FOUND'
    ) {
      setAccountDiscoveryStatus(AccountDiscoveryStatus.AccountsDiscovered);
      setShowHeader(true);
    }

    if (getTenant() === TenantType.MAHINDRA) {
      if (
        event.data.eventCode === 'CONSENT_APPROVED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_APPROVED_FAILED' ||
        event.data.eventCode === 'CONSENT_REJECTED_SUCCESS' ||
        event.data.eventCode === 'CONSENT_REJECTED_FAILED'
      ) {
        setIsLoading(true);
        setTimeout(() => {
          setViewType(VIEWTYPE.BANKSUMMARYVIEW);
        }, 2000);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleSDKEvents);

    if (getTenant() === TenantType.JFS) {
      setIsHeaderVisible(false);
      setIsBottomNavbarVisible(false);
    }

    return () => {
      window.removeEventListener('message', handleSDKEvents);

      if (getTenant() === TenantType.JFS) {
        // setIsHeaderVisible(true);
        // setIsBottomNavbarVisible(true);
      }
    };
  }, []);

  const handleBackButton = () => {
    gotoRoot();
    navigate('/home', { replace: true });
  };

  const renderAAJourney = () => {
    switch (getTenant()) {
      case TenantType.PB:
        return (
          <PBAA
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            gotoRoot={gotoRoot}
            handleBackButton={handleBackButton}
            isInfoAvailable={isInfoAvailable}
          />
        );

      case TenantType.YESBANK:
        return (
          <YesBankAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      case TenantType.MAHINDRA:
        return (
          <MahindraAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );
      case TenantType.JFS:
        return (
          <JFSAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      case TenantType.AU:
        return (
          <AUAA
            setIsInfoAvailable={setIsInfoAvailable}
            partialLoading={partialLoading}
            redirectionURL={redirectionURL}
            showHeader={showHeader}
            setPartialLoading={setPartialLoading}
            accountDiscoveryStatus={accountDiscoveryStatus}
            setAccountDiscoveryStatus={setAccountDiscoveryStatus}
          />
        );

      default:
        return <></>;
    }
  };

  return <>{renderAAJourney()}</>;
};

export default AAWrapper;
